/**
 * Úkoly API — typed wrappers around `/v1/tasks`.
 *
 * Mirrors the OpenAPI shapes (`TaskListResponse`, `TaskDetailResponse`,
 * company-users lookup) in `api/src/schemas/tasks.ts`. Re-declared here so the
 * module compiles when the generated `frontend/src/types/api.ts` hasn't been
 * refreshed.
 *
 * A task is standalone (no parent record) and carries two user references —
 * `id_zadal` (creator) and `id_prijemce` (assignee). The dashboard splits "my
 * tasks" (assigned to me) from "tasks I created" (for others) off those.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type TaskStav = 'aktivni' | 'prijato' | 'splneny' | 'zruseny';

export interface Task {
  id_ukolu: number;
  id_firmy: number;
  id_zadal: number;
  id_prijemce: number;
  nazev: string;
  popis: string | null;
  datum_pripominky: string | null;
  /** HH:MM time-of-day for the reminder; null = whole day. Only set with a date. */
  cas_pripominky: string | null;
  stav: TaskStav;
  /** 1 = private: self-assigned, visible only to its owner (API-enforced). */
  soukromy: number;
  datum_vytvoreni: string | null;
  datum_splneni: string | null;
  zadal_jmeno: string;
  zadal_barva: string | null;
  prijemce_jmeno: string;
  prijemce_barva: string | null;
}

export interface TaskListFilters {
  /** Restrict to tasks assigned to the current user. */
  mine?: boolean;
  assignee?: number | null;
  creator?: number | null;
  /** 'otevrene' = aktivní+přijato (open). Defaults to 'otevrene' server-side. */
  stav?: TaskStav | 'otevrene' | 'vse';
  name?: string;
}

interface TaskListResponse {
  tasks: Task[];
  total: number;
}

function buildQuery(filters: TaskListFilters): string {
  const params = new URLSearchParams();
  if (filters.mine) params.set('mine', '1');
  if (filters.assignee != null) params.set('assignee', String(filters.assignee));
  if (filters.creator != null) params.set('creator', String(filters.creator));
  if (filters.stav) params.set('stav', filters.stav);
  if (filters.name) params.set('name', filters.name);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useTasksQuery(filters: TaskListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['tasks', 'list', filters],
    queryFn: ({ signal }) =>
      api.get<TaskListResponse>(`/v1/tasks${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export interface TaskMessage {
  id: number;
  id_uzivatele: number;
  zprava: string;
  /** 1 = system entry (status change), 0 = user message. */
  system: number;
  datum_vytvoreni: string | null;
  autor_jmeno: string;
  autor_barva: string | null;
}

export interface TaskDetail extends Task {
  zpravy: TaskMessage[];
}

interface TaskDetailResponse {
  task: TaskDetail;
}

export function useTaskQuery(id: number | null) {
  return useQuery({
    queryKey: ['tasks', id],
    enabled: id != null && Number.isFinite(id),
    refetchOnMount: 'always',
    queryFn: ({ signal }) => api.get<TaskDetailResponse>(`/v1/tasks/${id}`, { signal }),
  });
}

export interface TaskUser {
  id: number;
  jmeno: string;
  prijmeni: string;
  barva: string | null;
}

interface TaskUsersResponse {
  users: TaskUser[];
}

export function useTaskUsersQuery() {
  return useQuery({
    queryKey: ['tasks', 'users'],
    queryFn: ({ signal }) => api.get<TaskUsersResponse>('/v1/tasks/users', { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

export interface TaskBody {
  nazev: string;
  popis?: string;
  datum_pripominky?: string;
  /** HH:MM; ignored server-side unless datum_pripominky is set. */
  cas_pripominky?: string;
  id_prijemce?: number;
  /** Private flag; the API ignores it unless the task is self-assigned. */
  soukromy?: boolean;
}

function invalidateTaskCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['tasks'] });
}

interface TaskCreatedResponse {
  id: number;
}

export function useCreateTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskBody) => api.post<TaskCreatedResponse>('/v1/tasks', body),
    onSuccess: () => invalidateTaskCaches(qc),
  });
}

export function useUpdateTaskMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskBody) => api.put<{ status: 'ok' }>(`/v1/tasks/${id}`, body),
    onSuccess: () => invalidateTaskCaches(qc),
  });
}

export function useAcceptTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ status: 'ok' }>(`/v1/tasks/${id}/accept`),
    onSuccess: () => invalidateTaskCaches(qc),
  });
}

export function useCompleteTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ status: 'ok' }>(`/v1/tasks/${id}/complete`),
    onSuccess: () => invalidateTaskCaches(qc),
  });
}

/** Post a message to a task's conversation thread (creator/assignee only). */
export function useCreateTaskMessageMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zprava: string) =>
      api.post<{ status: 'ok' }>(`/v1/tasks/${id}/messages`, { zprava }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', id] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useCancelTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ status: 'ok' }>(`/v1/tasks/${id}/cancel`),
    onSuccess: () => invalidateTaskCaches(qc),
  });
}

